<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle>{{ t('offerType.title') }}</FormTitle>
      <FormContent>
        <Space size="24px" direction="vertical">
          <FormRadioBoxItem
            v-for="item in radioItems"
            :key="item.value"
            :value="item.value"
            :label="t(item.label)"
            :name="t(item.label)"
            :checked="checkedOfferType"
            @onClick="onItemClick"
          />
          <HgTextArea
            v-if="checkForOtherSection()"
            :modelValue="formData.estimationReason ?? ''"
            :updateModelOnInput="false"
            name="estimationReason"
            :label="t('offerType.messageBoxTitle')"
            :placeholder="t('offerType.messageBoxText')"
            @input="onBlur"
          />
        </Space>
      </FormContent>
    </TransitionWrapper>
    <ValuationFunnelErrorText v-if="isShowErrorMessage" />
    <FormBottom v-if="checkForOtherSection()" :onNext="onNext" />
  </FormContainer>
</template>

<script lang="ts" setup>
import { HgTextArea } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormRadioBoxItem,
  FormTitle,
  Space,
  TransitionWrapper,
} from '@/components/common';
import ValuationFunnelErrorText from '@/components/common/ValuationFunnelErrorText/ValuationFunnelErrorText.vue';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { OfferType } from '@/models/valuationFunnel';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs } from '@/tracking/tracking';
import { ValuationFlow } from '@/types';

const propertyValuationStore = usePropertyValuationStore();
const { data: formData, step: formStep } = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

// Reactive data
const radioItems = ref([
  {
    label: 'offerType.selling',
    value: '0',
    trackingValue: 'sellOrBuy_buy',
  },
  {
    label: 'offerType.buying',
    value: '1',
    trackingValue: 'sellOrBuy_sell',
  },
  {
    label: 'offerType.other',
    value: '2',
    trackingValue: 'sellOrBuy_other',
  },
]);
const isShowErrorMessage = ref(false);
const checkedOfferType = computed(() =>
  formData.value.offerType === undefined ? '' : formData.value.offerType.toString(),
);

// Methods
const onItemClick = (value: string): void => {
  isShowErrorMessage.value = false;
  const offerType = parseInt(value);
  propertyValuationStore.setFormData({ offerType });
  if (offerType !== OfferType.Other) {
    onNext();
  }
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.OfferType);
};

const onNext = (): void => {
  if (formData.value.offerType === undefined) {
    isShowErrorMessage.value = true;
    return;
  }
  if (formData.value.offerType !== OfferType.Other) {
    propertyValuationStore.setFormData({ estimationReason: '' });
  }
  propertyValuationStore.navigateNext(ValuationFlow.OfferType);
};

const onBlur = (value: any): void => {
  propertyValuationStore.setFormData({
    estimationReason: value.target.value,
  });
};

const checkForOtherSection = (): boolean => {
  return formData.value.offerType === OfferType.Other;
};

// Lifecycle (replacing mounted)
onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_sellOrBuy',
    locale: locale.value,
    stepInputs: getStepInputs(formData.value, route.query.source?.toString() || '', formStep.value),
  });
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;
</style>
